import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Form, Input } from '@arco-design/web-react';

import WorkspaceItem, { GlobalWorkspaceItem } from '@/components-biz/WorkspaceItem';
import { getCurrCluster } from '@/service/index';

import { isFieldDisabled, isFieldReadonly } from '../../platform/capability-registry/fieldControl';
import { isFieldVisible } from '../../platform/capability-registry/resolveFieldMode';
import { buildOptionFromRaw, readString, syncOptionMetaFromRaw, useSyncOptionMetaField } from '../../platform/option-meta';
import { BASE_INFO_FIELD_KEYS, BASE_INFO_OPTION_META_KEYS } from './fieldKeys';
import styles from './index.module.less';
import type { BaseInfoBlockProps, BaseInfoPaths } from './types';

const FormItem = Form.Item;

const ALIAS_NAME_REG = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;

/** 基本信息能力块（名称 / 描述 / 租户空间 / 命名空间 / 数据中心 hidden） */
export function BaseInfoBlock(props: BaseInfoBlockProps) {
  const { form, paths: rawPaths, options, ...control } = props;
  const paths = rawPaths as BaseInfoPaths;
  const { t } = useTranslation();
  const params = useParams();

  const workspaceSource = options?.workspaceSource ?? 'binding';
  const lockWorkspaceFromRoute = options?.lockWorkspaceFromRoute !== false;
  const showWorkspaceLabel = options?.showWorkspaceLabel !== false;
  const isEdit = options?.isEdit === true;
  const editNameDisabled = isEdit && options?.allowEditName !== true;
  const nameMaxLength = options?.nameMaxLength ?? 63;
  const descriptionMaxLength = options?.descriptionMaxLength === false ? undefined : (options?.descriptionMaxLength ?? 256);
  const nameFieldKind = options?.nameField ?? 'name';

  const routeWorkspace = params?.tenant ?? params?.ws;
  const defaultWorkspace = options?.defaultWorkspace ?? routeWorkspace;
  const workspaceLocked = options?.lockWorkspace === true || (lockWorkspaceFromRoute && !!routeWorkspace);

  useEffect(() => {
    const current = form.getFieldValue(paths.datacenter);
    if (!current) {
      form.setFieldValue(paths.datacenter, getCurrCluster());
    }
  }, [form, paths.datacenter]);

  const handleWorkspaceChange = useCallback(
    (value: string, option?: { name?: string; displayName?: string; namespace?: string }) => {
      form.setFieldValue(paths.namespace, option?.namespace);
      syncOptionMetaFromRaw(
        form,
        value,
        {
          displayName: option?.displayName,
          namespace: option?.namespace,
        },
        BASE_INFO_OPTION_META_KEYS.workspace,
      );
    },
    [form, paths.namespace],
  );

  const nameReadonly = isFieldReadonly(BASE_INFO_FIELD_KEYS.name, control);
  const nameDisabled = isFieldDisabled(BASE_INFO_FIELD_KEYS.name, control);
  const descriptionReadonly = isFieldReadonly(BASE_INFO_FIELD_KEYS.description, control);
  const descriptionDisabled = isFieldDisabled(BASE_INFO_FIELD_KEYS.description, control);
  const workspaceReadonly = isFieldReadonly(BASE_INFO_FIELD_KEYS.workspace, control);
  const workspaceDisabled = isFieldDisabled(BASE_INFO_FIELD_KEYS.workspace, control);

  const nameLabel = useMemo(() => {
    if (nameFieldKind === 'nickname') {
      return t('common:nickname');
    }
    return t('common:name');
  }, [nameFieldKind, t]);

  const nameRules = useMemo(
    () =>
      nameReadonly
        ? undefined
        : [
            {
              required: true,
              validator(value: string | undefined, callback: (error?: string) => void) {
                if (!value) {
                  callback(nameFieldKind === 'nickname' ? t('data-center:please.enter.an.alias') : t('common:required.field'));
                  return;
                }
                if (value.length > nameMaxLength) {
                  callback(t('data-center:please.enter.a.name.up.to.63.characters.maximum'));
                  return;
                }
                // nickname 保留 K8s 风格格式校验；name / alias 与下单一致，只限制长度
                if (nameFieldKind === 'nickname' && !ALIAS_NAME_REG.test(value)) {
                  callback(t('data-center:invalid.alias.format'));
                  return;
                }
                callback();
              },
            },
          ],
    [nameFieldKind, nameMaxLength, nameReadonly, t],
  );

  const workspaceRules = useMemo(
    () => (workspaceReadonly ? undefined : [{ required: true, message: t('common:required.field') }]),
    [t, workspaceReadonly],
  );

  useSyncOptionMetaField(form, {
    field: paths.workspace,
    buildOption: (val) => {
      const text = readString(val);
      if (!text) return undefined;
      return buildOptionFromRaw(text, { name: text, label: text });
    },
    lookupKey: BASE_INFO_OPTION_META_KEYS.workspace,
  });

  const workspaceDisabledState = workspaceReadonly || workspaceDisabled || isEdit || workspaceLocked;

  const showWorkspaceField = isFieldVisible(BASE_INFO_FIELD_KEYS.workspace, control);

  const workspaceField =
    showWorkspaceField &&
    (workspaceSource === 'global' ? (
      <GlobalWorkspaceItem
        field={paths.workspace}
        rules={workspaceRules}
        defaultValue={defaultWorkspace}
        hidden={options?.hideWorkspace}
        showLabel={showWorkspaceLabel}
        disabled={workspaceDisabledState}
        onChange={handleWorkspaceChange}
      />
    ) : (
      <WorkspaceItem
        field={paths.workspace}
        rules={workspaceRules}
        defaultValue={defaultWorkspace}
        hidden={options?.hideWorkspace}
        showLabel={showWorkspaceLabel}
        disabled={workspaceDisabledState}
        onChange={handleWorkspaceChange}
      />
    ));

  if (
    !isFieldVisible(BASE_INFO_FIELD_KEYS.name, control) &&
    !isFieldVisible(BASE_INFO_FIELD_KEYS.description, control) &&
    !isFieldVisible(BASE_INFO_FIELD_KEYS.workspace, control) &&
    !isFieldVisible(BASE_INFO_FIELD_KEYS.namespace, control)
  ) {
    return null;
  }

  return (
    <>
      {isFieldVisible(BASE_INFO_FIELD_KEYS.name, control) ? (
        <FormItem
          label={nameLabel}
          field={paths.name}
          rules={nameRules}
          disabled={nameDisabled || editNameDisabled}
          className={styles.formItem}
        >
          <Input
            maxLength={nameMaxLength}
            placeholder={nameFieldKind === 'nickname' ? t('data-center:please.enter.an.alias') : t('common:input.placeholder')}
            disabled={nameReadonly || nameDisabled || editNameDisabled}
            readOnly={nameReadonly}
          />
        </FormItem>
      ) : null}

      {isFieldVisible(BASE_INFO_FIELD_KEYS.description, control) ? (
        <FormItem label={t('common:description')} field={paths.description} disabled={descriptionDisabled} className={styles.formItem}>
          <Input.TextArea
            placeholder={t('common:description.placeholder')}
            maxLength={descriptionMaxLength}
            rows={options?.descriptionRows}
            disabled={descriptionReadonly || descriptionDisabled}
            readOnly={descriptionReadonly}
          />
        </FormItem>
      ) : null}
      {showWorkspaceField && workspaceField}
      <FormItem field={paths.namespace} hidden>
        <Input />
      </FormItem>
      <FormItem field={paths.datacenter} hidden className={styles.hiddenField}>
        <Input />
      </FormItem>
    </>
  );
}
